import {
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  Loader,
  Modal,
  Pagination,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { SessionRepairStatusBadge } from '../../research/packages/session-repair';
import { deriveSessionRepairStatus } from '../../research/packages/session-repair/utils/deriveSessionRepairStatus';
import { useSessionPickerCatalog } from '../hooks/useSessionPickerCatalog';
import type { ResearchConfigurationLiteItem } from '../../../types/Configuration';

const formatDate = (value?: string | Date | null) => {
  if (!value) return 'n/a';
  return new Date(value).toLocaleString('it-IT');
};

type SessionPickerModalProps = {
  opened: boolean;
  onClose: () => void;
  userId?: string;
  existingSessionIds: number[];
  onAddSessions: (
    entries: Array<{
      sessionId: number;
      patientCode?: string | null;
      hasHrData?: boolean;
      hasHrDataRepaired?: boolean;
    }>
  ) => void;
};

export function SessionPickerModal({
  opened,
  onClose,
  userId,
  existingSessionIds,
  onAddSessions,
}: SessionPickerModalProps) {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [searchId, setSearchId] = useState('');
  const [searchPatient, setSearchPatient] = useState('');
  const [searchDate, setSearchDate] = useState('');
  const [page, setPage] = useState(1);
  const [debouncedSearchId] = useDebouncedValue(searchId, 250);
  const [debouncedSearchPatient] = useDebouncedValue(searchPatient, 250);
  const [debouncedSearchDate] = useDebouncedValue(searchDate, 250);

  const pageSize = 25;
  const filters = useMemo(
    () => ({
      searchId: debouncedSearchId,
      searchPatient: debouncedSearchPatient,
      searchDate: debouncedSearchDate,
    }),
    [debouncedSearchDate, debouncedSearchId, debouncedSearchPatient]
  );

  const {
    pageItems,
    totalAvailable,
    totalPages,
    safePage,
    getSessionById,
    isLoading,
    isFetching,
  } = useSessionPickerCatalog({
    userId,
    filters,
    existingSessionIds,
    page,
    pageSize,
    enabled: opened,
  });

  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const resetModal = () => {
    setSelectedIds([]);
    setSearchId('');
    setSearchPatient('');
    setSearchDate('');
    setPage(1);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const toggleSession = (sessionId: number) => {
    setSelectedIds(current =>
      current.includes(sessionId)
        ? current.filter(id => id !== sessionId)
        : [...current, sessionId]
    );
  };

  const handleConfirm = () => {
    const entries = selectedIds
      .map(sessionId => {
        const session = getSessionById(sessionId);
        return {
          sessionId,
          patientCode: session?.patientCode,
          hasHrData: session?.hasHrData,
          hasHrDataRepaired: session?.hasHrDataRepaired,
        };
      })
      .filter(entry => entry.sessionId != null);

    if (entries.length === 0) return;

    onAddSessions(entries);
    handleClose();
  };

  const emptyMessage =
    totalAvailable === 0
      ? 'Tutte le sessioni completed sono già nel gruppo.'
      : 'Nessuna sessione disponibile in questa pagina.';

  const selectablePageIds = useMemo(
    () =>
      pageItems.filter(session => session.hasHrData).map(session => session.id),
    [pageItems]
  );

  const allPageSelected =
    selectablePageIds.length > 0 &&
    selectablePageIds.every(sessionId => selectedIds.includes(sessionId));

  const somePageSelected =
    selectablePageIds.some(sessionId => selectedIds.includes(sessionId)) &&
    !allPageSelected;

  const toggleCurrentPageSelection = () => {
    if (allPageSelected) {
      const pageIdSet = new Set(pageItems.map(session => session.id));
      setSelectedIds(current => current.filter(id => !pageIdSet.has(id)));
      return;
    }

    setSelectedIds(current =>
      Array.from(new Set([...current, ...selectablePageIds]))
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title='Aggiungi sessioni completed'
      size='80vw'
    >
      <Stack gap='md'>
        <Card withBorder>
          <Stack gap='sm'>
            <Group grow>
              <TextInput
                label='Cerca per ID'
                value={searchId}
                onChange={event => {
                  setSearchId(event.currentTarget.value);
                  setPage(1);
                }}
                leftSection={<Search size={16} />}
              />
              <TextInput
                label='Cerca per utente'
                value={searchPatient}
                onChange={event => {
                  setSearchPatient(event.currentTarget.value);
                  setPage(1);
                }}
              />
              <TextInput
                label='Cerca per data'
                value={searchDate}
                onChange={event => {
                  setSearchDate(event.currentTarget.value);
                  setPage(1);
                }}
              />
            </Group>

            <Text size='sm' c='dimmed'>
              {selectedIds.length} sessioni selezionate
            </Text>

            <ScrollArea h='65vh'>
              {isLoading ? (
                <Group justify='center' py='xl'>
                  <Loader size='sm' />
                </Group>
              ) : (
                <Stack gap='sm'>
                  {isFetching && (
                    <Group justify='center'>
                      <Loader size='xs' />
                    </Group>
                  )}
                  <Table striped highlightOnHover stickyHeader>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>
                          <Checkbox
                            checked={allPageSelected}
                            indeterminate={somePageSelected}
                            disabled={selectablePageIds.length === 0}
                            onChange={toggleCurrentPageSelection}
                            aria-label='Seleziona pagina corrente'
                          />
                        </Table.Th>
                        <Table.Th>ID</Table.Th>
                        <Table.Th>Utente</Table.Th>
                        <Table.Th>Record biometrici</Table.Th>
                        <Table.Th>Stato</Table.Th>
                        <Table.Th>Data</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {pageItems.length === 0 ? (
                        <Table.Tr>
                          <Table.Td colSpan={6}>
                            <Text size='sm' c='dimmed' ta='center' py='md'>
                              {emptyMessage}
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      ) : (
                        pageItems.map(
                          (session: ResearchConfigurationLiteItem) => {
                            const selected = selectedIds.includes(session.id);
                            const repairStatus = deriveSessionRepairStatus({
                              hasHrData: session.hasHrData,
                              hasHrDataRepaired: session.hasHrDataRepaired,
                            });

                            return (
                              <Table.Tr key={session.id}>
                                <Table.Td>
                                  <Checkbox
                                    checked={selected}
                                    onChange={() => toggleSession(session.id)}
                                    disabled={!session.hasHrData}
                                  />
                                </Table.Td>
                                <Table.Td>{session.id}</Table.Td>
                                <Table.Td>
                                  {session.patientCode ?? 'n/a'}
                                </Table.Td>
                                <Table.Td>
                                  <Badge
                                    color={session.hasHrData ? 'teal' : 'red'}
                                    variant='light'
                                  >
                                    {session.hasHrData ? 'Presenti' : 'Assenti'}
                                  </Badge>
                                </Table.Td>
                                <Table.Td>
                                  <SessionRepairStatusBadge
                                    status={repairStatus}
                                  />
                                </Table.Td>
                                <Table.Td>
                                  {formatDate(
                                    session.started_at ??
                                      session.created_at ??
                                      session.updated_at
                                  )}
                                </Table.Td>
                              </Table.Tr>
                            );
                          }
                        )
                      )}
                    </Table.Tbody>
                  </Table>
                  {totalPages > 1 && (
                    <Group justify='space-between' align='center'>
                      <Text size='xs' c='dimmed'>
                        {isFetching
                          ? '…'
                          : `${totalAvailable} sessioni disponibili`}
                      </Text>
                      <Pagination
                        value={safePage}
                        onChange={setPage}
                        total={totalPages}
                        size='sm'
                      />
                    </Group>
                  )}
                  {totalPages <= 1 && totalAvailable > 0 && (
                    <Text size='xs' c='dimmed'>
                      {isFetching
                        ? '…'
                        : `${totalAvailable} sessioni disponibili`}
                    </Text>
                  )}
                </Stack>
              )}
            </ScrollArea>
          </Stack>
        </Card>

        <Group justify='flex-end'>
          <Button variant='subtle' onClick={handleClose}>
            Annulla
          </Button>
          <Button onClick={handleConfirm} disabled={selectedIds.length === 0}>
            Aggiungi sessioni
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
